import AdminService from "@/services/admin-service";
import { MatchEvent } from "@/model/models";
import { offsetIsoMinutes } from "@/utils/general-functions";

// Match events logic for the Daily page: assignments, channel links, setup limits. Mixed into Daily.vue: reads its state through `this`.
export default {
  computed: {
    // { [eventId]: Event }
    eventsById() {
      const map = {};
      for (const ev of this.eventsList) map[ev.eventId] = ev;
      return map;
    },
    // { [application]: Event[] }
    eventsByApplication() {
      const map = {};
      for (const ev of this.eventsList) {
        const app = ev.application || '—';
        if (!map[app]) map[app] = [];
        map[app].push(ev);
      }
      return map;
    },
    // { [calMatchId]: MatchEvent[] }
    matchEventsByMatch() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!map[me.calMatchId]) map[me.calMatchId] = [];
        map[me.calMatchId].push(me);
      }
      return map;
    },
    // { [matchEventId]: calChannelId[] }
    channelLinksByMatchEvent() {
      const map = {};
      for (const link of this.calEventChannelsList) {
        if (!link.active) continue;
        if (!map[link.matchEventId]) map[link.matchEventId] = [];
        map[link.matchEventId].push(link.calChannelId);
      }
      return map;
    },
    // { [calMatchId]: { [application]: { [calChannelId]: matchEventId } } } - see
    // MatchEvents.vue's own channelClaimsByMatch for the full "why" (same channel can't
    // carry two events of the same application on the same match at once).
    channelClaimsByMatch() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!me.active || !me.eventId) continue;
        const application = this.eventsById[me.eventId]?.application;
        if (!application) continue;
        const linkedIds = this.channelLinksByMatchEvent[me.matchEventId] || [];
        if (!linkedIds.length) continue;
        if (!map[me.calMatchId]) map[me.calMatchId] = {};
        if (!map[me.calMatchId][application]) map[me.calMatchId][application] = {};
        for (const calChannelId of linkedIds) {
          map[me.calMatchId][application][calChannelId] = me.matchEventId;
        }
      }
      return map;
    },
    // { [eventId]: [{ calMatchId, busyStart, busyEnd }, ...] } - every active match_events
    // assignment's busy window. Daily always covers every league at once (Martin: "all
    // leagues mixed together, yes"), so - unlike MatchEvents.vue's per-league mode - there's
    // no separate cross-league fetch needed; matchEventsList already carries every league's
    // rows. The one real gap: this can only see a conflict on a match that's actually loaded
    // (see doLoadAll's date-window fetch) - a clash with a match far outside this window
    // won't be caught here the way the full Match Events page would catch it. Flagged as a
    // BE follow-up (a date-scoped "occupied events" endpoint) rather than silently ignored.
    takenEventWindowsByEvent() {
      const map = {};
      for (const me of this.matchEventsList) {
        if (!me.eventId || !me.active) continue;
        const match = this.calMatchesList.find(m => m.calMatchId === me.calMatchId);
        if (!match || !match.startTime) continue;
        if (!map[me.eventId]) map[me.eventId] = [];
        map[me.eventId].push({
          calMatchId: me.calMatchId,
          // Each assignment's own setup time, not the calendar-wide ceiling.
          busyStart: new Date(offsetIsoMinutes(match.startTime, -(me.eventPadSetup ?? this.calendarStatic.maxPadSetupMinutes))).getTime(),
          busyEnd: new Date(match.endTime || match.startTime).getTime()
        });
      }
      return map;
    }
  },
  methods: {
    // "#12 - Order Number 3" - same convention as MatchEventSlotEditor.vue's own eventLabel,
    // deliberately not the event's current team matchup (that rotates independently of which
    // physical slot this is).
    eventLabel(eventId) {
      const ev = this.eventsById[eventId];
      if (!ev) return `#${eventId}`;
      if (ev.orderNumber === undefined || ev.orderNumber === null) return `#${eventId}`;
      return `#${eventId} - Order ${ev.orderNumber}`;
    },
    // Builds one EventUsageTimeline bar. Unlike buildTimelineBar's shadow (which extends on
    // both sides of the match), an event's shadow only leads into the match start - it's the
    // assignment's own padSetup minutes - the same window the event conflict check uses. colorSlot is the SAME per-match slot ChannelUsageTimeline
    // uses, not a per-application color - application identity is already carried by the
    // group header, and one match reading as one color across both timelines is the point.
    buildEventBar(matchEvent, match) {
      const winStart = this.dayStart.getTime();
      const winEnd = this.dayEnd.getTime();
      const total = winEnd - winStart;
      const matchStart = new Date(match.startTime).getTime();
      const matchEnd = new Date(this.effectiveEnd(match)).getTime();
      const padMinutes = Math.max(matchEvent.eventPadSetup || 0, 0);
      const rawShadowStart = matchStart - padMinutes * 60 * 1000;
      const clampPct = (t) => ((Math.min(Math.max(t, winStart), winEnd) - winStart) / total) * 100;
      const barLeft = clampPct(matchStart);
      const barRight = clampPct(matchEnd);
      const shadowLeft = clampPct(rawShadowStart);
      const label = `${match.team1} v ${match.team2}`;
      const tooltipLines = [
        `${this.eventLabel(matchEvent.eventId)} \u2013 ${label}`,
        `${this.formatLocalTime(match.startTime)}\u2013${this.formatLocalTime(this.effectiveEnd(match))}`
      ];
      if (padMinutes > 0) tooltipLines.push(`Setup: ${padMinutes}m before start`);
      if (!matchEvent.active) tooltipLines.push('Event assignment inactive');
      // An active assignment no channel carries yet: drawn grey so it reads as not ready to publish.
      const noChannels = matchEvent.active && !(this.channelLinksByMatchEvent[matchEvent.matchEventId] || []).length;
      if (noChannels) tooltipLines.push('No channel configured');
      return {
        calMatchId: match.calMatchId,
        left: barLeft,
        width: matchStart >= winEnd ? 0 : Math.max(barRight - barLeft, 0.4),
        shadowLeft,
        shadowWidth: Math.max(barLeft - shadowLeft, 0),
        cutLeft: matchStart < winStart,
        cutRight: matchEnd > winEnd,
        shadowCutLeft: rawShadowStart < winStart,
        colorSlot: this.matchColorByMatchId[match.calMatchId],
        inactive: !matchEvent.active,
        noChannels,
        label: noChannels ? `\u26A0 ${label} (no channel configured)` : label,
        tooltip: tooltipLines.join('\n')
      };
    },
    // --- Events - copied from MatchEvents.vue, unchanged behavior ----------------------
    isMatchEventConfigured(me) {
      return !!(me && me.eventId && me.active);
    },
    configuredEventCountFor(calMatchId) {
      return (this.matchEventsByMatch[calMatchId] || []).filter(this.isMatchEventConfigured).length;
    },
    applicationCountsForMatch(calMatchId) {
      const rows = (this.matchEventsByMatch[calMatchId] || []).filter(me => me.eventId && me.active);
      const counts = {};
      for (const me of rows) {
        const app = this.eventsById[me.eventId]?.application || '—';
        if (!counts[app]) counts[app] = { events: 0, channels: 0 };
        counts[app].events += 1;
        counts[app].channels += (this.channelLinksByMatchEvent[me.matchEventId] || []).length;
      }
      return Object.keys(counts).sort().map(application => ({ application, ...counts[application] }));
    },
    visibleMatchEventRows(calMatchId) {
      return (this.matchEventsByMatch[calMatchId] || [])
          .filter(me => !this.removedMatchEventIds[me.matchEventId]);
    },
    getVisibleEventSlotCount(calMatchId) {
      if (this.visibleEventSlotCount[calMatchId] !== undefined) {
        return this.visibleEventSlotCount[calMatchId];
      }
      return Math.max(this.visibleMatchEventRows(calMatchId).length, 1);
    },
    visibleEventSlotsFor(match) {
      const rows = this.visibleMatchEventRows(match.calMatchId);
      const visibleCount = this.getVisibleEventSlotCount(match.calMatchId);
      const slots = rows.map((me, i) => ({ key: 'me-' + me.matchEventId, index: i + 1, matchEvent: me }));
      if (!this.matchEditable(match)) return slots;
      for (let i = slots.length; i < visibleCount; i++) {
        slots.push({ key: 'new-me-' + i, index: i + 1, matchEvent: null });
      }
      return slots;
    },
    addEventSlot(calMatchId) {
      const current = this.getVisibleEventSlotCount(calMatchId);
      this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: current + 1 };
    },
    channelsClaimedElsewhere(calMatchId, eventId, excludeMatchEventId) {
      const application = this.eventsById[eventId]?.application;
      if (!application) return [];
      const claims = (this.channelClaimsByMatch[calMatchId] || {})[application] || {};
      return Object.entries(claims)
          .filter(([, matchEventId]) => matchEventId !== excludeMatchEventId)
          .map(([calChannelId]) => Number(calChannelId));
    },
    onSaveChannels(matchEventId, { calChannelIds }) {
      if (!matchEventId) return;
      this.channelLinkSaving = { ...this.channelLinkSaving, [matchEventId]: true };
      this.channelLinkErrors = { ...this.channelLinkErrors, [matchEventId]: '' };
      AdminService.setCalEventChannels(matchEventId, calChannelIds).then(() => {
        return this.refetchCalEventChannels();
      }).catch((error) => {
        this.channelLinkErrors = { ...this.channelLinkErrors, [matchEventId]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.channelLinkSaving = { ...this.channelLinkSaving, [matchEventId]: false };
      });
    },
    upsertMatchEventRow(calMatchId, payload) {
      const existing = payload.matchEventId
          ? this.matchEventsList.find(me => me.matchEventId === payload.matchEventId)
          : this.matchEventsList.find(me => me.calMatchId === calMatchId && me.eventId === payload.eventId);

      const matchEvent = new MatchEvent(
          existing ? existing.matchEventId : undefined,
          calMatchId,
          payload.eventId,
          payload.padSetup,
          existing ? existing.status : false,
          payload.active
      );
      return AdminService.upsertMatchEvent(matchEvent).then(() => matchEvent);
    },
    refetchMatchEvents() {
      const { from, to } = this.fetchWindow();
      return AdminService.getAllMatchEvents(from, to).then((matchEvents) => {
        this.matchEventsList = matchEvents;
      });
    },
    saveMatchEvent(calMatchId, payload) {
      return this.upsertMatchEventRow(calMatchId, payload).then((matchEvent) => {
        return this.refetchMatchEvents().then(() => matchEvent);
      });
    },
    onMatchEventSave(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      this.matchEventSaving = { ...this.matchEventSaving, [key]: true };
      this.matchEventErrors = { ...this.matchEventErrors, [key]: '' };
      this.saveMatchEvent(calMatchId, payload).then(() => {
        if (payload.active === false) return;
        const saved = this.matchEventsList.find(me => me.calMatchId === calMatchId && me.eventId === payload.eventId && me.active);
        if (saved && (this.channelLinksByMatchEvent[saved.matchEventId] || []).length === 0) {
          this.autoOpenChannels = { ...this.autoOpenChannels, [calMatchId + '-' + payload.eventId]: true };
        }
      }).catch((error) => {
        this.matchEventErrors = { ...this.matchEventErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchEventSaving = { ...this.matchEventSaving, [key]: false };
      });
    },
    onMatchEventRemove(calMatchId, slotKey, payload) {
      const key = `${calMatchId}-${slotKey}`;
      const matchEventId = payload.matchEventId;
      const previousCount = this.getVisibleEventSlotCount(calMatchId);
      this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: Math.max(previousCount - 1, 0) };

      if (!matchEventId) return;

      this.removedMatchEventIds = { ...this.removedMatchEventIds, [matchEventId]: true };
      this.matchEventSaving = { ...this.matchEventSaving, [key]: true };
      this.matchEventErrors = { ...this.matchEventErrors, [key]: '' };
      this.saveMatchEvent(calMatchId, { ...payload, active: false }).catch((error) => {
        const restored = { ...this.removedMatchEventIds };
        delete restored[matchEventId];
        this.removedMatchEventIds = restored;
        this.visibleEventSlotCount = { ...this.visibleEventSlotCount, [calMatchId]: previousCount };
        this.matchEventErrors = { ...this.matchEventErrors, [key]: this.extractErrorMessage(error) };
      }).finally(() => {
        this.matchEventSaving = { ...this.matchEventSaving, [key]: false };
      });
    },
    takenEventWindowsForMatch(match, ownMatchEventId) {
      // Busy = another assignment's [its start - its own setup, its end] overlaps this match
      // itself. This match's own setup isn't part of the test - it's capped instead, by
      // eventPadLimitsForMatch, to end no earlier than the previous match on that event.
      const thisBusyStart = new Date(match.startTime).getTime();
      const thisBusyEnd = new Date(match.endTime || match.startTime).getTime();
      const result = {};
      for (const [eventId, windows] of Object.entries(this.takenEventWindowsByEvent)) {
        const conflict = windows.find(w =>
            w.calMatchId !== match.calMatchId &&
            thisBusyStart < w.busyEnd && w.busyStart < thisBusyEnd
        );
        if (conflict) {
          let label;
          if (conflict.calMatchId !== null) {
            const conflictMatch = this.calMatchesList.find(m => m.calMatchId === conflict.calMatchId);
            label = conflictMatch ? this.matchLabel(conflictMatch) : `Match #${conflict.calMatchId}`;
          } else {
            label = `another league, until ${this.formatLocalHm(conflict.busyEnd)}`;
          }
          result[eventId] = { calMatchId: conflict.calMatchId, label };
        }
      }
      for (const me of (this.matchEventsByMatch[match.calMatchId] || [])) {
        if (!me.eventId || !me.active || me.matchEventId === ownMatchEventId) continue;
        if (result[me.eventId]) continue;
        result[me.eventId] = { calMatchId: match.calMatchId, label: 'another slot on this match' };
      }
      return result;
    },
    // { [eventId]: { maxPad, label } } - for events that are free during this match: the most
    // setup time this match can take on that event without reaching back into the previous
    // match that uses it (setup may start, at the earliest, when that match ends). Listed only
    // when that gap is below maxPadSetupMinutes. MatchEventSlotEditor caps its setup input and
    // defaults a fresh pick to this value.
    eventPadLimitsForMatch(match) {
      const thisStart = new Date(match.startTime).getTime();
      const ceiling = this.calendarStatic.maxPadSetupMinutes;
      const result = {};
      for (const [eventId, windows] of Object.entries(this.takenEventWindowsByEvent)) {
        let prev = null;
        for (const w of windows) {
          if (w.calMatchId === match.calMatchId || w.busyEnd > thisStart) continue;
          if (!prev || w.busyEnd > prev.busyEnd) prev = w;
        }
        if (!prev) continue;
        const maxPad = Math.min(ceiling, Math.floor((thisStart - prev.busyEnd) / 60000));
        if (maxPad >= ceiling) continue;
        const prevMatch = prev.calMatchId !== null ? this.calMatchesList.find(m => m.calMatchId === prev.calMatchId) : null;
        const who = prevMatch ? this.matchLabel(prevMatch) : (prev.calMatchId !== null ? `Match #${prev.calMatchId}` : 'another league');
        result[eventId] = { maxPad, label: `${who} ends ${this.formatLocalHm(prev.busyEnd)}` };
      }
      return result;
    }
  }
};
